/**
 * Quality Co-Pilot — structured quality-issue ("complaint") form helpers.
 *
 * Kept in a sibling `.ts` (not the `.tsx`) so the helpers are pure + unit-testable
 * and the component module stays export-only (the project's react-refresh rule).
 *
 * This is the "type, don't dictate" path: a CSR / quality user fills the same field
 * set the Co-Pilot would otherwise gather by voice, then we COMPOSE the fields into a
 * single message and send it through the SAME flow voice/free-text use (send →
 * streamCopilotMessage). The recorder agent records it as a quality signal — no
 * backend change.
 *
 * Field parity is intentional and verified against the two systems of record:
 *   - the canonical intake contract `register_quality_issue` / `IssueDraft`
 *     (services/quality/agent_write_tools.py + write_tools.py → the `quality_issues`
 *     table, db/models/quality.py), AND
 *   - Adam's legacy sales-site form `chemmas_sales.product_packaging_feedback`,
 *     which the production sync maps INTO `quality_issues` via
 *     services/quality/legacy_packaging_mapper.py.
 * Every field below maps to one of those; see the docstring on each.
 */

import type {
  QualityIssueAttachmentRef,
  QualityIssueSubmitPayload,
} from '../services/qualityCopilot';

export interface ComplaintIssueCategory {
  value: string;
  label: string;
}

/**
 * The six canonical `QualityIssueCategory` values (db/models/quality.py). Adam's
 * free-text `complaint` tags (contents/abnormal/packaging/shipping/damage/billing/
 * application/label/color/leak/shortage) all collapse into these via
 * legacy_packaging_mapper.CATEGORY_MAP — so offering these six is exact parity.
 */
export const ISSUE_CATEGORIES: ReadonlyArray<ComplaintIssueCategory> = [
  { value: 'packaging', label: 'Packaging / container damage' },
  { value: 'quality', label: 'Product quality / performance' },
  { value: 'shipping', label: 'Shipping / short shipment' },
  { value: 'billing', label: 'Billing' },
  { value: 'application', label: 'Application failure (jobsite)' },
  { value: 'other', label: 'Other' },
];

/** Canonical `QualityIssueSeverity` values (db/models/quality.py). */
export const SEVERITIES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

/** One affected product line — mirrors a `product_skus[]` entry (item_code + qty). */
export interface ComplaintProduct {
  sku: string;
  quantity: string; // free text ("10", "12 drums"); optional
}

/** The structured fields a user fills in the typed complaint form. */
export interface ComplaintFormFields {
  customer: string; // → customer_no (acting_for_customer)
  products: ComplaintProduct[]; // → product_skus[] (multiple, like Adam's child table)
  issueCategory: string; // → category
  severity: string; // → severity
  orderRef: string; // → related_sage_so_no (Sage SO# / PO)
  batch: string; // → extra.lot_number
  observedAt: string; // → occurred_at (ISO date from <input type="date">)
  jobsite: string; // → jobsite
  businessImpact: string; // → business_impact
  contact: string; // → csr_contact ("who were you talking to" / sales_team_member)
  photos: File[]; // → attachments[] (uploaded to GCS; named in the message)
  description: string; // → description (complaint_text + customer_comment)
}

/** An empty form. Starts with one blank product row for an obvious affordance. */
export const EMPTY_COMPLAINT_FORM: ComplaintFormFields = {
  customer: '',
  products: [{ sku: '', quantity: '' }],
  issueCategory: '',
  severity: 'medium',
  orderRef: '',
  batch: '',
  observedAt: '',
  jobsite: '',
  businessImpact: '',
  contact: '',
  photos: [],
  description: '',
};

/** Human label for a category value (falls back to the raw value). */
export function issueCategoryLabel(value: string): string {
  return ISSUE_CATEGORIES.find((c) => c.value === value)?.label ?? value;
}

/** Human label for a severity value (falls back to the raw value). */
export function severityLabel(value: string): string {
  return SEVERITIES.find((s) => s.value === value)?.label ?? value;
}

/**
 * Validate the required fields. Returns the first human-readable error, or null
 * when the form is fileable. Required: customer, issue category, description.
 * Severity has a default; everything else (products, order ref, lot, date, jobsite,
 * business impact, contact, photos) is "if known" — the eval cases routinely have
 * fuzzy/missing facts and the agent files what is reliably known.
 */
export function complaintFormError(f: ComplaintFormFields): string | null {
  if (!f.customer.trim()) return 'Enter the customer name (or who reported it).';
  if (!f.issueCategory.trim()) return 'Choose an issue category.';
  if (!f.description.trim()) return 'Describe what happened.';
  return null;
}

/** Non-blank product lines only (drops empty repeater rows). */
export function filledProducts(f: ComplaintFormFields): ComplaintProduct[] {
  return f.products.filter((p) => p.sku.trim());
}

/**
 * Compose the structured fields into the message the Co-Pilot receives. Only filled
 * optional fields are emitted, so the agent isn't fed empty "Jobsite: —" noise. The
 * labels mirror the `register_quality_issue` params so the recorder maps them
 * cleanly. Photos are NOTED by count rather than attached — the channel is text-only
 * today and the eval set expects pending photos to be acknowledged, not blocked on.
 */
export function composeComplaintPrompt(f: ComplaintFormFields): string {
  const lines: string[] = ['I want to file a quality issue. Here are the details:'];
  lines.push(`- Customer: ${f.customer.trim()}`);
  for (const p of filledProducts(f)) {
    const qty = p.quantity.trim();
    lines.push(`- Product / SKU: ${p.sku.trim()}${qty ? ` — qty ${qty}` : ''}`);
  }
  lines.push(`- Issue category: ${issueCategoryLabel(f.issueCategory)}`);
  lines.push(`- Severity: ${severityLabel(f.severity)}`);
  if (f.orderRef.trim()) lines.push(`- Order reference (Sage SO#/PO): ${f.orderRef.trim()}`);
  if (f.batch.trim()) lines.push(`- Batch / lot #: ${f.batch.trim()}`);
  if (f.observedAt.trim()) lines.push(`- Observed: ${f.observedAt.trim()}`);
  if (f.jobsite.trim()) lines.push(`- Jobsite: ${f.jobsite.trim()}`);
  if (f.businessImpact.trim()) lines.push(`- Business impact: ${f.businessImpact.trim()}`);
  if (f.contact.trim()) lines.push(`- Contact (who reported): ${f.contact.trim()}`);
  if (f.photos.length > 0) {
    const names = f.photos.map((p) => p.name).join(', ');
    lines.push(`- Photos: ${f.photos.length} attached (${names}).`);
  }
  lines.push(`- Details: ${f.description.trim()}`);
  return lines.join('\n');
}

/** Map the form fields + uploaded photo refs onto the structured submit payload. */
export function complaintFormToPayload(
  f: ComplaintFormFields,
  attachments: QualityIssueAttachmentRef[],
  conversationId: string | null,
): QualityIssueSubmitPayload {
  return {
    customer: f.customer.trim(),
    category: f.issueCategory,
    severity: f.severity,
    description: f.description.trim(),
    products: filledProducts(f).map((p) => {
      const qty = p.quantity.trim();
      return qty ? { item_code: p.sku.trim(), quantity: qty } : { item_code: p.sku.trim() };
    }),
    related_sage_so_no: f.orderRef.trim() || null,
    lot_number: f.batch.trim() || null,
    occurred_at: f.observedAt.trim() || null,
    jobsite: f.jobsite.trim() || null,
    business_impact: f.businessImpact.trim() || null,
    contact: f.contact.trim() || null,
    attachments,
    conversation_id: conversationId,
  };
}
